import { DropFile } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { Cross } from "components/icons/Cross";
import { UnifiedPreview } from "components/UnifiedPreview/UnifiedPreview";
import { TASK_COMPLETED_FILE_DOC_TYPE } from "constants/task";
import { FieldArray, useFormikContext } from "formik";
import React, { useState } from "react";

export type InkleFileData = {
  name: string;
  type: string;
  uuid?: string;
};

const FILE_OPTIONS = convertDisplayToOptions(TASK_COMPLETED_FILE_DOC_TYPE);

const handleFileDrop = (
  newFiles: File[] | FileType[],
  index: number,
  setFieldValue: (field: string, value: any) => void
) => {
  const newFile = newFiles[0];
  if (!newFile) return;

  if (!("uuid" in newFile)) {
    setFieldValue(`files.${index}.file`, newFile);
  } else {
    setFieldValue(`files.${index}.file`, {
      name: newFile.name,
      type: newFile.file_type,
      uuid: newFile.uuid,
    });
  }
};

export const FileCompleteDocuments = ({ groupId }: { groupId: string }) => {
  const { values, setFieldValue } = useFormikContext<{
    files: {
      file: File | InkleFileData | null;
      docType: string;
    }[];
  }>();
  const [localPreviewFile, setLocalPreviewFile] = useState<File | null>(null);
  const [remotePreviewFile, setRemotePreviewFile] =
    useState<InkleFileData | null>(null);

  const onPreview = ({ file }: { file: File | InkleFileData | null }) => {
    if ((file as InkleFileData)?.uuid) {
      setRemotePreviewFile(file);
    } else if (file) {
      setLocalPreviewFile(file as File);
    }
  };

  const handlePreviewClose = () => {
    setLocalPreviewFile(null);
    setRemotePreviewFile(null);
  };

  const existingDocTypes = values.files.map((f) => f.docType);

  const updatedFileOptions = FILE_OPTIONS.map((option) => ({
    ...option,
    isDisabled:
      option.value !== "OTHERS" && existingDocTypes.includes(option.value),
  }));

  return (
    <FieldArray
      name="files"
      render={(arrayHelpers) => (
        <div>
          {values.files.map(({ file, docType }, index) => {
            const selected = FILE_OPTIONS.find(
              (option) => option.value === docType
            );
            const dataExist = Boolean(file || docType);

            return (
              <div key={index} className="t-flex t-gap-3 t-mb-3 t-items-start">
                <div className="t-flex-1 t-basis-0 t-min-w-0">
                  <Combobox
                    name={`files.${index}.docType`}
                    label="Select document type"
                    options={updatedFileOptions}
                    placeholder="Select type"
                    menuPortalTarget={document.body}
                    value={selected}
                    required={dataExist}
                    withForm
                  />
                </div>
                <div className="t-flex-1 t-basis-0 t-min-w-0">
                  <FileInput
                    required={dataExist}
                    onDelete={() => setFieldValue(`files.${index}.file`, null)}
                    multiple={false}
                    file={
                      file
                        ? ({
                            ...file,
                            name: file.name,
                            is_previewable: true,
                            file_type: file.type,
                            uuid: (file as InkleFileData).uuid || "",
                          } as DropFile)
                        : null
                    }
                    shouldUpdateFieldOnDelete
                    onDrop={(newFiles) =>
                      handleFileDrop(newFiles, index, setFieldValue)
                    }
                    withInkleDocs
                    label="Upload document"
                    accept={{
                      "image/*": [".png", ".jpeg", ".jpg", ".webp", ".avif"],
                      "application/pdf": [".pdf"],
                      "application/vnd.ms-excel": [".xls"],
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                        [".xlsx"],
                      "text/csv": [".csv"],
                    }}
                    block
                    name={`files.${index}.file`}
                    withForm
                    onFileClick={() => onPreview({ file })}
                  />
                </div>
                {values.files.length > 1 && (
                  <div className="t-mt-6">
                    <Button
                      type="button"
                      customType="ghost_icon"
                      onClick={(e) => arrayHelpers.remove(index)}
                    >
                      <span className="t-text-text-30">
                        <Cross size="20" />
                      </span>
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          <Button
            type="button"
            size="small"
            customType="secondary"
            onClick={() =>
              arrayHelpers.push({
                file: null,
                docType: "",
              })
            }
          >
            Upload document
          </Button>
          <UnifiedPreview
            localPreviewFile={localPreviewFile}
            remotePreviewFile={remotePreviewFile}
            closeModal={handlePreviewClose}
            groupId={groupId}
          />
        </div>
      )}
    />
  );
};
